import React, { useEffect, useMemo, useState } from "react";

/**
 * CRM Ativo (Protótipo) - 1 arquivo JSX
 * - Foco: fila de retornos (telemarketing + WhatsApp tasks)
 * - Substitui: tela Refiltek Telemarketing + tarefas Digisac
 *
 * Como usar:
 * - Crie um arquivo: src/pages/ActiveCRMPrototype.jsx
 * - Importe e renderize numa rota (React Router) ou direto no App.
 *
 * Dependências: nenhuma (só React).
 * Observação: estilos são injetados via <style> dentro do componente.
 */

const MOCK_USERS = [
  { id: "u1", name: "Agente 01" },
  { id: "u2", name: "Agente 02" },
  { id: "u3", name: "Supervisora" },
];

const STATUS = [
  "Novo",
  "Sem retorno",
  "Em negociação",
  "Aguardando pagamento",
  "Fechado - venda",
  "Perdido",
  "Pós-venda",
];

const CHANNELS = ["WhatsApp", "Ligação", "E-mail", "Presencial"];

const OUTCOMES = [
  "Cliente respondeu",
  "Cliente não respondeu",
  "Sem interesse",
  "Pediu orçamento",
  "Negociando",
  "Fechou",
  "Agendar depois",
  "Número inválido",
];

function addHours(date, h) {
  const d = new Date(date);
  d.setHours(d.getHours() + h);
  return d;
}
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}
function toISOInputValue(date) {
  // yyyy-MM-ddTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}
function formatBR(date) {
  const d = new Date(date);
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
function isSameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}
function hoursDiff(a, b) {
  // a - b in hours
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  return (da - db) / (1000 * 60 * 60);
}

const now = new Date();
const MOCK_ITEMS = [
  {
    id: "c-1001",
    customer: {
      name: "João Pereira",
      phone: "+55 (62) 9xxxx-1111",
      city: "Goiânia",
      store: "Matriz",
      segment: "Residencial",
      tags: ["Filtro", "Troca"],
    },
    status: "Sem retorno",
    priority: "Alta",
    channel: "WhatsApp",
    assignedTo: "u1",
    source: "WhatsApp inbound",
    lastContactAt: addHours(now, -30),
    nextActionAt: addHours(now, -2), // atrasado
    lastNote: "Pediu prazo para decidir. Retornar hoje.",
    history: [
      {
        at: addHours(now, -30),
        by: "Agente 01",
        channel: "WhatsApp",
        outcome: "Cliente respondeu",
        note: "Cliente pediu orçamento do refil X. Enviado.",
      },
    ],
  },
  {
    id: "c-1002",
    customer: {
      name: "Maria Souza",
      phone: "+55 (62) 9xxxx-2222",
      city: "Aparecida",
      store: "Loja 02",
      segment: "Comercial",
      tags: ["Máquina de gelo", "Instalação"],
    },
    status: "Em negociação",
    priority: "Média",
    channel: "Ligação",
    assignedTo: "u2",
    source: "Lead (campanha)",
    lastContactAt: addHours(now, -5),
    nextActionAt: addHours(now, 3), // hoje
    lastNote: "Confirmar voltagem e local de instalação.",
    history: [
      {
        at: addHours(now, -5),
        by: "Agente 02",
        channel: "Ligação",
        outcome: "Negociando",
        note: "Cliente quer 2 opções de pagamento. Retornar após almoço.",
      },
    ],
  },
  {
    id: "c-1003",
    customer: {
      name: "Carlos Mendes",
      phone: "+55 (62) 9xxxx-3333",
      city: "Anápolis",
      store: "Loja 04",
      segment: "Residencial",
      tags: ["Purificador", "Orçamento"],
    },
    status: "Novo",
    priority: "Baixa",
    channel: "WhatsApp",
    assignedTo: "u1",
    source: "Site",
    lastContactAt: addHours(now, -1),
    nextActionAt: addHours(now, 20), // amanhã
    lastNote: "Lead novo do site, enviar apresentação.",
    history: [
      {
        at: addHours(now, -1),
        by: "Agente 01",
        channel: "WhatsApp",
        outcome: "Agendar depois",
        note: "Cliente pediu contato amanhã de manhã.",
      },
    ],
  },
  {
    id: "c-1004",
    customer: {
      name: "Oficina XPTO",
      phone: "+55 (62) 3xxx-4444",
      city: "Goiânia",
      store: "Matriz",
      segment: "Comercial",
      tags: ["Bebedouro industrial", "Manutenção"],
    },
    status: "Aguardando pagamento",
    priority: "Alta",
    channel: "WhatsApp",
    assignedTo: "u3",
    source: "Retorno (pós-orçamento)",
    lastContactAt: addHours(now, -18),
    nextActionAt: addDays(now, 2),
    lastNote: "Cobrar confirmação do pagamento / envio do comprovante.",
    history: [
      {
        at: addHours(now, -18),
        by: "Supervisora",
        channel: "WhatsApp",
        outcome: "Pediu orçamento",
        note: "Orçamento aprovado. Aguardando pagamento.",
      },
    ],
  },
];

function Badge({ children, tone = "neutral" }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

function Button({ children, onClick, variant = "primary", disabled }) {
  return (
    <button
      className={`btn btn--${variant}`}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        className="input"
        value={value}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, allowEmpty = true }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {allowEmpty && <option value="">Todos</option>}
        {options.map((opt) => (
          <option key={String(opt.value ?? opt)} value={String(opt.value ?? opt)}>
            {String(opt.label ?? opt)}
          </option>
        ))}
      </select>
    </label>
  );
}

function Modal({ title, open, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal__header">
          <div className="modal__title">{title}</div>
          <button className="iconBtn" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className="modal__body">{children}</div>
        <div className="modal__footer">{footer}</div>
      </div>
    </div>
  );
}

function Drawer({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="drawerOverlay" role="dialog" aria-modal="true">
      <div className="drawer">
        <div className="drawer__header">
          <div className="drawer__title">Cliente</div>
          <button className="iconBtn" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className="drawer__body">{children}</div>
      </div>
    </div>
  );
}

function slaTone(nextActionAt) {
  const diff = hoursDiff(nextActionAt, new Date()); // future positive, past negative
  if (diff < -0.01) return "danger";
  if (isSameDay(nextActionAt, new Date())) return "warning";
  return "success";
}

function slaLabel(nextActionAt) {
  const diff = hoursDiff(nextActionAt, new Date());
  if (diff < -0.01) return "Atrasado";
  if (isSameDay(nextActionAt, new Date())) return "Hoje";
  return "Futuro";
}

export default function ActiveCRMPrototype() {
  const [items, setItems] = useState(MOCK_ITEMS);

  // filtros
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [channel, setChannel] = useState("");
  const [priority, setPriority] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  // seleção
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) || null,
    [items, selectedId]
  );

  // modais
  const [openLog, setOpenLog] = useState(false);
  const [openNewTask, setOpenNewTask] = useState(false);

  // form registrar contato
  const [logOutcome, setLogOutcome] = useState("Cliente respondeu");
  const [logNote, setLogNote] = useState("");
  const [logNextActionAt, setLogNextActionAt] = useState(
    toISOInputValue(addHours(new Date(), 4))
  );
  const [logStatus, setLogStatus] = useState("");

  // form criar tarefa
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCity, setNewCity] = useState("Goiânia");
  const [newStore, setNewStore] = useState("Matriz");
  const [newChannel, setNewChannel] = useState("WhatsApp");
  const [newAssigned, setNewAssigned] = useState("u1");
  const [newPriority, setNewPriority] = useState("Média");
  const [newStatus, setNewStatus] = useState("Novo");
  const [newNextActionAt, setNewNextActionAt] = useState(
    toISOInputValue(addHours(new Date(), 2))
  );
  const [newNote, setNewNote] = useState("");

  // inicializa logStatus com base no item selecionado
  useEffect(() => {
    if (!selected) return;
    setLogStatus(selected.status);
    setLogOutcome("Cliente respondeu");
    setLogNote("");
    setLogNextActionAt(toISOInputValue(addHours(new Date(), 4)));
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items
      .filter((i) => {
        const matchesQ =
          !qq ||
          i.customer.name.toLowerCase().includes(qq) ||
          i.customer.phone.toLowerCase().includes(qq) ||
          i.customer.city.toLowerCase().includes(qq) ||
          i.customer.tags.join(" ").toLowerCase().includes(qq) ||
          i.id.toLowerCase().includes(qq);

        const matchesStatus = !status || i.status === status;
        const matchesUser = !assignedTo || i.assignedTo === assignedTo;
        const matchesChannel = !channel || i.channel === channel;
        const matchesPriority = !priority || i.priority === priority;

        const overdue = new Date(i.nextActionAt) < new Date();
        const matchesOverdue = !onlyOverdue || overdue;

        return (
          matchesQ &&
          matchesStatus &&
          matchesUser &&
          matchesChannel &&
          matchesPriority &&
          matchesOverdue
        );
      })
      .sort((a, b) => new Date(a.nextActionAt) - new Date(b.nextActionAt)); // fila por vencimento
  }, [items, q, status, assignedTo, channel, priority, onlyOverdue]);

  const kpis = useMemo(() => {
    const now = new Date();
    const overdue = items.filter((i) => new Date(i.nextActionAt) < now).length;
    const today = items.filter((i) => isSameDay(i.nextActionAt, now)).length;
    const next7 = items.filter((i) => {
      const d = new Date(i.nextActionAt);
      return d >= now && d <= addDays(now, 7);
    }).length;
    const negotiating = items.filter((i) => i.status === "Em negociação").length;
    const waitingPay = items.filter(
      (i) => i.status === "Aguardando pagamento"
    ).length;

    return { overdue, today, next7, negotiating, waitingPay };
  }, [items]);

  function userName(id) {
    return MOCK_USERS.find((u) => u.id === id)?.name || "-";
  }

  function openSelected(id) {
    setSelectedId(id);
  }

  function quickAction(kind) {
    if (!selected) return;
    // Aqui você pluga Digisac/WhatsApp API, telefonia, etc.
    // No protótipo: só demonstra comportamento.
    alert(
      `Ação: ${kind}\nCliente: ${selected.customer.name}\nCanal sugerido: ${selected.channel}`
    );
  }

  function saveLog() {
    if (!selected) return;

    const entry = {
      at: new Date(),
      by: "Você",
      channel: selected.channel,
      outcome: logOutcome,
      note: logNote || "(sem observação)",
    };

    const next = new Date(logNextActionAt);

    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== selected.id) return i;
        return {
          ...i,
          status: logStatus || i.status,
          lastContactAt: new Date(),
          nextActionAt: next,
          lastNote: logNote || i.lastNote,
          history: [entry, ...i.history],
        };
      })
    );

    setOpenLog(false);
  }

  function createTask() {
    if (!newName.trim() || !newPhone.trim()) {
      alert("Preencha ao menos Nome e Telefone.");
      return;
    }
    const id = `c-${Math.floor(1000 + Math.random() * 9000)}`;
    const item = {
      id,
      customer: {
        name: newName.trim(),
        phone: newPhone.trim(),
        city: newCity,
        store: newStore,
        segment: "Residencial",
        tags: [],
      },
      status: newStatus,
      priority: newPriority,
      channel: newChannel,
      assignedTo: newAssigned,
      source: "Cadastro manual",
      lastContactAt: null,
      nextActionAt: new Date(newNextActionAt),
      lastNote: newNote || "Criado no CRM ativo",
      history: [
        {
          at: new Date(),
          by: "Você",
          channel: newChannel,
          outcome: "Agendar depois",
          note: newNote || "Criado no CRM ativo",
        },
      ],
    };

    setItems((prev) => [item, ...prev]);
    setOpenNewTask(false);
    setSelectedId(id);

    // reset básico
    setNewName("");
    setNewPhone("");
    setNewNote("");
    setNewNextActionAt(toISOInputValue(addHours(new Date(), 2)));
  }

  return (
    <div className="page">
      <style>{css}</style>

      <header className="topbar">
        <div className="topbar__left">
          <div className="titleBlock">
            <div className="title">CRM Ativo</div>
            <div className="subtitle">
              Fila de retornos • WhatsApp/Ligação • SLA e responsáveis
            </div>
          </div>
        </div>

        <div className="topbar__right">
          <div className="searchWrap">
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por cliente, telefone, cidade, tag, ID…"
            />
          </div>
          <Button variant="secondary" onClick={() => setOpenNewTask(true)}>
            + Novo retorno
          </Button>
        </div>
      </header>

      <section className="kpiRow">
        <div className="kpiCard kpiCard--danger">
          <div className="kpiLabel">Atrasadas</div>
          <div className="kpiValue">{kpis.overdue}</div>
          <div className="kpiHint">vencidas (SLA estourado)</div>
        </div>
        <div className="kpiCard kpiCard--warning">
          <div className="kpiLabel">Para hoje</div>
          <div className="kpiValue">{kpis.today}</div>
          <div className="kpiHint">vencem hoje</div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">Próx. 7 dias</div>
          <div className="kpiValue">{kpis.next7}</div>
          <div className="kpiHint">janela de follow-up</div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">Em negociação</div>
          <div className="kpiValue">{kpis.negotiating}</div>
          <div className="kpiHint">pipeline ativo</div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">Aguard. pagamento</div>
          <div className="kpiValue">{kpis.waitingPay}</div>
          <div className="kpiHint">cobrança / confirmação</div>
        </div>
      </section>

      <section className="content">
        <aside className="filters">
          <div className="panelTitle">Filtros</div>

          <Select
            label="Status"
            value={status}
            onChange={setStatus}
            options={STATUS}
          />
          <Select
            label="Responsável"
            value={assignedTo}
            onChange={setAssignedTo}
            options={MOCK_USERS.map((u) => ({ value: u.id, label: u.name }))}
          />
          <Select
            label="Canal"
            value={channel}
            onChange={setChannel}
            options={CHANNELS}
          />
          <Select
            label="Prioridade"
            value={priority}
            onChange={setPriority}
            options={["Alta", "Média", "Baixa"]}
          />

          <label className="check">
            <input
              type="checkbox"
              checked={onlyOverdue}
              onChange={(e) => setOnlyOverdue(e.target.checked)}
            />
            <span>Somente atrasadas</span>
          </label>

          <div className="divider" />

          <div className="panelTitle">Ações rápidas</div>
          <div className="stack">
            <Button
              variant="ghost"
              onClick={() => {
                setStatus("");
                setAssignedTo("u1");
              }}
            >
              Minhas tarefas
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOnlyOverdue(true);
              }}
            >
              Ver atrasadas
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setQ("");
                setStatus("");
                setAssignedTo("");
                setChannel("");
                setPriority("");
                setOnlyOverdue(false);
              }}
            >
              Limpar filtros
            </Button>
          </div>

          <div className="hintBox">
            <div className="hintTitle">Ideia do CRM ativo</div>
            <div className="hintText">
              Tudo gira em torno da <b>próxima ação</b> (nextActionAt). Isso
              substitui “tarefa do Digisac” e vira o motor do acompanhamento.
            </div>
          </div>
        </aside>

        <main className="list">
          <div className="listHeader">
            <div className="listTitle">
              Fila de retornos <span className="muted">({filtered.length})</span>
            </div>
            <div className="listLegend">
              <Badge tone="danger">Atrasado</Badge>
              <Badge tone="warning">Hoje</Badge>
              <Badge tone="success">Futuro</Badge>
            </div>
          </div>

          <div className="table">
            <div className="row row--head">
              <div>Cliente</div>
              <div>Status</div>
              <div>Responsável</div>
              <div>Canal</div>
              <div>Próxima ação</div>
              <div>Prioridade</div>
            </div>

            {filtered.map((i) => {
              const tone = slaTone(i.nextActionAt);
              const label = slaLabel(i.nextActionAt);
              const overdue = new Date(i.nextActionAt) < new Date();
              return (
                <button
                  key={i.id}
                  className={`row row--item ${overdue ? "row--overdue" : ""}`}
                  onClick={() => openSelected(i.id)}
                  type="button"
                >
                  <div className="cellMain">
                    <div className="cellTitle">
                      {i.customer.name} <span className="muted">• {i.id}</span>
                    </div>
                    <div className="cellSub">
                      {i.customer.phone} • {i.customer.city} • {i.customer.store}
                    </div>
                    <div className="tagRow">
                      {i.customer.tags.slice(0, 3).map((t) => (
                        <span key={t} className="tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Badge tone="neutral">{i.status}</Badge>
                  </div>

                  <div className="muted">{userName(i.assignedTo)}</div>

                  <div>
                    <Badge tone="neutral">{i.channel}</Badge>
                  </div>

                  <div>
                    <div className="cellTitle">{formatBR(i.nextActionAt)}</div>
                    <div className="cellSub">
                      <Badge tone={tone}>{label}</Badge>
                    </div>
                  </div>

                  <div>
                    <Badge
                      tone={
                        i.priority === "Alta"
                          ? "danger"
                          : i.priority === "Média"
                          ? "warning"
                          : "neutral"
                      }
                    >
                      {i.priority}
                    </Badge>
                  </div>
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div className="empty">
                Nada encontrado com os filtros atuais.
              </div>
            )}
          </div>
        </main>
      </section>

      <Drawer open={!!selected} onClose={() => setSelectedId(null)}>
        {!selected ? null : (
          <div className="drawerContent">
            <div className="drawerTop">
              <div className="drawerName">{selected.customer.name}</div>
              <div className="drawerMeta">
                <span className="muted">{selected.customer.phone}</span>
                <span className="dot">•</span>
                <span className="muted">{selected.customer.city}</span>
                <span className="dot">•</span>
                <span className="muted">{selected.customer.store}</span>
              </div>

              <div className="drawerBadges">
                <Badge tone="neutral">{selected.status}</Badge>
                <Badge tone={slaTone(selected.nextActionAt)}>
                  {slaLabel(selected.nextActionAt)}
                </Badge>
                <Badge tone="neutral">{selected.channel}</Badge>
              </div>
            </div>

            <div className="drawerActions">
              <Button variant="primary" onClick={() => setOpenLog(true)}>
                Registrar contato
              </Button>
              <Button variant="secondary" onClick={() => quickAction("WhatsApp")}>
                WhatsApp
              </Button>
              <Button variant="secondary" onClick={() => quickAction("Ligar")}>
                Ligar
              </Button>
              <Button variant="secondary" onClick={() => quickAction("Template")}>
                Disparar template
              </Button>
            </div>

            <div className="drawerSection">
              <div className="sectionTitle">Resumo</div>
              <div className="kv">
                <div className="kvRow">
                  <div className="kvKey">Responsável</div>
                  <div className="kvVal">{userName(selected.assignedTo)}</div>
                </div>
                <div className="kvRow">
                  <div className="kvKey">Fonte</div>
                  <div className="kvVal">{selected.source}</div>
                </div>
                <div className="kvRow">
                  <div className="kvKey">Último contato</div>
                  <div className="kvVal">
                    {selected.lastContactAt ? formatBR(selected.lastContactAt) : "-"}
                  </div>
                </div>
                <div className="kvRow">
                  <div className="kvKey">Próxima ação</div>
                  <div className="kvVal">{formatBR(selected.nextActionAt)}</div>
                </div>
                <div className="kvRow">
                  <div className="kvKey">Obs.</div>
                  <div className="kvVal">{selected.lastNote}</div>
                </div>
              </div>
            </div>

            <div className="drawerSection">
              <div className="sectionTitle">Histórico</div>
              <div className="timeline">
                {selected.history.map((h, idx) => (
                  <div className="timelineItem" key={idx}>
                    <div className="timelineTop">
                      <div className="timelineAt">{formatBR(h.at)}</div>
                      <div className="timelineBy">{h.by}</div>
                    </div>
                    <div className="timelineBadges">
                      <Badge tone="neutral">{h.channel}</Badge>
                      <Badge tone="neutral">{h.outcome}</Badge>
                    </div>
                    <div className="timelineNote">{h.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title="Registrar contato"
        open={openLog}
        onClose={() => setOpenLog(false)}
        footer={
          <div className="modalFooter">
            <Button variant="ghost" onClick={() => setOpenLog(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={saveLog} disabled={!selected}>
              Salvar registro
            </Button>
          </div>
        }
      >
        {!selected ? (
          <div className="muted">Selecione um cliente na fila.</div>
        ) : (
          <div className="grid2">
            <Select
              label="Resultado do contato"
              value={logOutcome}
              onChange={setLogOutcome}
              options={OUTCOMES}
              allowEmpty={false}
            />
            <Select
              label="Atualizar status"
              value={logStatus}
              onChange={setLogStatus}
              options={STATUS}
              allowEmpty={false}
            />
            <label className="field">
              <span className="field__label">Próxima ação (agendamento)</span>
              <input
                className="input"
                type="datetime-local"
                value={logNextActionAt}
                onChange={(e) => setLogNextActionAt(e.target.value)}
              />
            </label>

            <label className="field field--full">
              <span className="field__label">Observações</span>
              <textarea
                className="textarea"
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                placeholder="Ex.: cliente pediu desconto, retorno amanhã 09:00…"
              />
            </label>

            <div className="callout field--full">
              Dica: este registro é o que vira “log de telemarketing” + “tarefa
              do Digisac”. Você salva o resultado e já agenda a próxima ação.
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Novo retorno"
        open={openNewTask}
        onClose={() => setOpenNewTask(false)}
        footer={
          <div className="modalFooter">
            <Button variant="ghost" onClick={() => setOpenNewTask(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={createTask}>
              Criar
            </Button>
          </div>
        }
      >
        <div className="grid2">
          <Input
            label="Nome do cliente"
            value={newName}
            onChange={setNewName}
            placeholder="Ex.: José da Silva"
          />
          <Input
            label="Telefone"
            value={newPhone}
            onChange={setNewPhone}
            placeholder="+55 (62) 9xxxx-xxxx"
          />

          <Input label="Cidade" value={newCity} onChange={setNewCity} />
          <Input label="Loja" value={newStore} onChange={setNewStore} />

          <Select
            label="Canal"
            value={newChannel}
            onChange={setNewChannel}
            options={CHANNELS}
            allowEmpty={false}
          />
          <Select
            label="Responsável"
            value={newAssigned}
            onChange={setNewAssigned}
            options={MOCK_USERS.map((u) => ({ value: u.id, label: u.name }))}
            allowEmpty={false}
          />

          <Select
            label="Prioridade"
            value={newPriority}
            onChange={setNewPriority}
            options={["Alta", "Média", "Baixa"]}
            allowEmpty={false}
          />
          <Select
            label="Status"
            value={newStatus}
            onChange={setNewStatus}
            options={STATUS}
            allowEmpty={false}
          />

          <label className="field field--full">
            <span className="field__label">Próxima ação (agendamento)</span>
            <input
              className="input"
              type="datetime-local"
              value={newNextActionAt}
              onChange={(e) => setNewNextActionAt(e.target.value)}
            />
          </label>

          <label className="field field--full">
            <span className="field__label">Observação</span>
            <textarea
              className="textarea"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Motivo do retorno, contexto, proposta…"
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}

const css = `
  :root{
    --bg:#0b0f16;
    --panel:#0f1623;
    --panel2:#0c1320;
    --border:rgba(255,255,255,.08);
    --text:rgba(255,255,255,.92);
    --muted:rgba(255,255,255,.60);
    --muted2:rgba(255,255,255,.45);
    --shadow: 0 18px 40px rgba(0,0,0,.45);
  }
  *{ box-sizing:border-box; }
  body{ background:var(--bg); color:var(--text); }
  .page{
    min-height:100vh;
    padding:20px;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
  }

  .topbar{
    display:flex; align-items:center; justify-content:space-between;
    gap:16px; padding:16px; border:1px solid var(--border);
    border-radius:16px; background:linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.03));
    box-shadow: var(--shadow);
  }
  .title{ font-size:22px; font-weight:800; letter-spacing:.2px; }
  .subtitle{ font-size:13px; color:var(--muted); margin-top:2px; }
  .topbar__right{ display:flex; gap:10px; align-items:center; }
  .searchWrap{ width:min(520px, 44vw); }

  .kpiRow{
    margin-top:14px;
    display:grid;
    grid-template-columns: repeat(5, minmax(140px, 1fr));
    gap:10px;
  }
  @media (max-width: 1100px){
    .kpiRow{ grid-template-columns: repeat(2, 1fr); }
    .searchWrap{ width: 100%; }
    .topbar{ flex-direction:column; align-items:flex-start; }
    .topbar__right{ width:100%; }
  }

  .kpiCard{
    border:1px solid var(--border);
    background:var(--panel);
    border-radius:16px;
    padding:14px;
  }
  .kpiCard--danger{ outline: 1px solid rgba(255,70,70,.25); }
  .kpiCard--warning{ outline: 1px solid rgba(255,190,70,.25); }
  .kpiLabel{ font-size:12px; color:var(--muted); }
  .kpiValue{ font-size:28px; font-weight:900; margin-top:2px; }
  .kpiHint{ font-size:12px; color:var(--muted2); margin-top:2px; }

  .content{
    margin-top:14px;
    display:grid;
    grid-template-columns: 320px 1fr;
    gap:12px;
    align-items:start;
  }
  @media (max-width: 1100px){
    .content{ grid-template-columns: 1fr; }
  }

  .filters{
    border:1px solid var(--border);
    background:var(--panel);
    border-radius:16px;
    padding:14px;
    position:sticky;
    top:14px;
  }
  .panelTitle{ font-weight:800; margin-bottom:10px; }
  .divider{ height:1px; background:var(--border); margin:12px 0; }

  .field{ display:flex; flex-direction:column; gap:6px; margin-bottom:10px; }
  .field__label{ font-size:12px; color:var(--muted); }
  .input{
    width:100%;
    padding:10px 12px;
    border-radius:12px;
    border:1px solid var(--border);
    background:var(--panel2);
    color:var(--text);
    outline:none;
  }
  .input:focus{ border-color: rgba(255,255,255,.18); }
  .textarea{
    width:100%;
    min-height:92px;
    padding:10px 12px;
    border-radius:12px;
    border:1px solid var(--border);
    background:var(--panel2);
    color:var(--text);
    outline:none;
    resize:vertical;
  }

  .check{
    display:flex; align-items:center; gap:10px;
    color:var(--muted); font-size:13px;
    user-select:none;
  }
  .stack{ display:flex; flex-direction:column; gap:8px; }

  .hintBox{
    margin-top:12px;
    padding:12px;
    border:1px dashed rgba(255,255,255,.16);
    border-radius:14px;
    background: rgba(255,255,255,.03);
  }
  .hintTitle{ font-weight:800; font-size:12px; margin-bottom:4px; }
  .hintText{ color:var(--muted); font-size:12px; line-height:1.35; }

  .list{
    border:1px solid var(--border);
    background:var(--panel);
    border-radius:16px;
    padding:14px;
    box-shadow: var(--shadow);
  }
  .listHeader{
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; margin-bottom:12px;
  }
  .listTitle{ font-weight:900; }
  .muted{ color:var(--muted); }
  .listLegend{ display:flex; gap:8px; align-items:center; }

  .table{ border:1px solid var(--border); border-radius:14px; overflow:hidden; }
  .row{
    display:grid;
    grid-template-columns: 2.2fr 1fr 1fr 1fr 1.2fr .8fr;
    gap:12px;
    padding:12px;
    align-items:center;
  }
  @media (max-width: 1100px){
    .row{ grid-template-columns: 1.6fr 1fr 1fr; }
    .row > div:nth-child(4),
    .row > div:nth-child(5),
    .row > div:nth-child(6){ display:none; }
  }
  .row--head{
    background: rgba(255,255,255,.04);
    color: var(--muted);
    font-size:12px;
    font-weight:700;
  }
  .row--item{
    width:100%;
    background:transparent;
    border:0;
    cursor:pointer;
    color:inherit;
    text-align:left;
    border-top:1px solid var(--border);
  }
  .row--item:hover{ background: rgba(255,255,255,.03); }
  .row--overdue{ background: rgba(255,70,70,.06); }

  .cellMain{ display:flex; flex-direction:column; gap:4px; }
  .cellTitle{ font-weight:800; }
  .cellSub{ color:var(--muted); font-size:12px; }
  .tagRow{ display:flex; gap:6px; flex-wrap:wrap; margin-top:4px; }
  .tag{
    font-size:11px; color: rgba(255,255,255,.72);
    padding:4px 8px; border-radius:999px;
    border:1px solid rgba(255,255,255,.12);
    background: rgba(255,255,255,.03);
  }

  .badge{
    display:inline-flex; align-items:center;
    padding:5px 10px;
    border-radius:999px;
    border:1px solid var(--border);
    background: rgba(255,255,255,.04);
    font-size:12px;
    color: var(--text);
    width: fit-content;
  }
  .badge--danger{
    border-color: rgba(255,70,70,.35);
    background: rgba(255,70,70,.12);
  }
  .badge--warning{
    border-color: rgba(255,190,70,.35);
    background: rgba(255,190,70,.12);
  }
  .badge--success{
    border-color: rgba(70,255,150,.28);
    background: rgba(70,255,150,.10);
  }
  .badge--neutral{ }

  .btn{
    border:1px solid var(--border);
    border-radius:12px;
    padding:10px 12px;
    cursor:pointer;
    font-weight:800;
    background: rgba(255,255,255,.04);
    color: var(--text);
  }
  .btn:disabled{ opacity:.5; cursor:not-allowed; }
  .btn--primary{
    background: rgba(120,180,255,.18);
    border-color: rgba(120,180,255,.35);
  }
  .btn--secondary{
    background: rgba(255,255,255,.06);
  }
  .btn--ghost{
    background: transparent;
  }

  .empty{ padding:14px; color:var(--muted); }

  .modalOverlay, .drawerOverlay{
    position:fixed; inset:0;
    background: rgba(0,0,0,.55);
    display:flex;
    align-items:center;
    justify-content:center;
    padding:16px;
    z-index:50;
  }
  .modal{
    width:min(760px, 95vw);
    background: var(--panel);
    border:1px solid var(--border);
    border-radius:16px;
    box-shadow: var(--shadow);
    overflow:hidden;
  }
  .modal__header{
    padding:14px 14px;
    display:flex; align-items:center; justify-content:space-between;
    border-bottom:1px solid var(--border);
  }
  .modal__title{ font-weight:900; }
  .modal__body{ padding:14px; }
  .modal__footer{
    padding:14px;
    border-top:1px solid var(--border);
    display:flex; justify-content:flex-end;
  }
  .modalFooter{ display:flex; gap:10px; justify-content:flex-end; width:100%; }
  .grid2{
    display:grid;
    grid-template-columns: 1fr 1fr;
    gap:10px;
  }
  @media (max-width: 900px){
    .grid2{ grid-template-columns: 1fr; }
  }
  .field--full{ grid-column: 1 / -1; }
  .callout{
    padding:12px;
    border:1px dashed rgba(255,255,255,.18);
    border-radius:14px;
    background: rgba(255,255,255,.03);
    color: var(--muted);
    font-size:12px;
  }

  .iconBtn{
    background: transparent;
    border: 1px solid var(--border);
    border-radius: 10px;
    width: 34px; height: 34px;
    cursor: pointer;
    color: var(--text);
  }

  .drawerOverlay{
    justify-content:flex-end;
    align-items:stretch;
    padding:0;
  }
  .drawer{
    width:min(520px, 95vw);
    background: var(--panel);
    border-left: 1px solid var(--border);
    box-shadow: var(--shadow);
    display:flex;
    flex-direction:column;
  }
  .drawer__header{
    padding:14px;
    display:flex; justify-content:space-between; align-items:center;
    border-bottom:1px solid var(--border);
  }
  .drawer__title{ font-weight:900; }
  .drawer__body{
    padding:14px;
    overflow:auto;
  }

  .drawerTop{ display:flex; flex-direction:column; gap:6px; }
  .drawerName{ font-size:18px; font-weight:900; }
  .drawerMeta{ color: var(--muted); font-size:12px; display:flex; gap:6px; flex-wrap:wrap; }
  .dot{ color: rgba(255,255,255,.28); }

  .drawerBadges{ display:flex; gap:8px; flex-wrap:wrap; margin-top:6px; }
  .drawerActions{ display:flex; gap:10px; flex-wrap:wrap; margin:12px 0; }

  .drawerSection{ margin-top:14px; padding-top:14px; border-top:1px solid var(--border); }
  .sectionTitle{ font-weight:900; margin-bottom:10px; }

  .kv{ display:flex; flex-direction:column; gap:8px; }
  .kvRow{ display:flex; justify-content:space-between; gap:12px; }
  .kvKey{ color: var(--muted); font-size:12px; }
  .kvVal{ font-size:12px; text-align:right; max-width: 320px; }

  .timeline{ display:flex; flex-direction:column; gap:10px; }
  .timelineItem{
    padding:12px;
    border:1px solid var(--border);
    background: rgba(255,255,255,.03);
    border-radius:14px;
  }
  .timelineTop{ display:flex; justify-content:space-between; gap:8px; align-items:center; }
  .timelineAt{ color: var(--muted); font-size:12px; }
  .timelineBy{ font-weight:800; font-size:12px; }
  .timelineBadges{ display:flex; gap:8px; flex-wrap:wrap; margin-top:8px; }
  .timelineNote{ margin-top:8px; color: rgba(255,255,255,.82); font-size:12px; line-height:1.35; }
`;
